import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import Helper from '../helpers';
import { isPublicRoute } from '../Services/SiteService.ts';

const AuthGuard = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const location = useLocation();

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const token = await Helper.getCookie('wh-jwt');
                
                if (token) {
                    // Verify token is not expired
                    const decoded = Helper.parseJwt(token);
                    const currentTime = Date.now() / 1000;
                    
                    if (decoded.exp > currentTime) {
                        setIsAuthenticated(true);
                    } else {
                        // Token is expired, remove it
                        await Helper.logout();
                        setIsAuthenticated(false);
                    }
                } else {
                    setIsAuthenticated(false);
                }
            } catch (error) {
                console.error('Auth check failed:', error);
                setIsAuthenticated(false);
            } finally {
                setIsLoading(false);
            }
        };

        checkAuth();
    }, [location.pathname]);

    // Show loading spinner while checking authentication
    if (isLoading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
            </div>
        );
    }

    // Check if current route is public
    const isCurrentRoutePublic = isPublicRoute(location.pathname);

    // If route is public, always allow access
    if (isCurrentRoutePublic) {
        return children;
    }

    // For private routes, redirect to login if not authenticated
    if (!isAuthenticated) {
        // Remember the page they wanted so login can take them back to it instead of the dashboard
        const wanted = `${location.pathname}${location.search}`;
        const loginPath = wanted && wanted !== '/' ? `/login?returnUrl=${encodeURIComponent(wanted)}` : '/login';
        return <Navigate to={loginPath} state={{ from: location }} replace />;
    }

    // User is authenticated and route is private, render children
    return children;
};

export default AuthGuard;
